import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { dashboardQueryOptions } from './dashboardQueryOptions'
import { Vehicle } from '@otw/models'
import {
  DASHBOARD_ACTIVE_QUERY_KEY,
  DASHBOARD_PURCHASED_FOR_INVENTORY_QUERY_KEY,
  DASHBOARD_READY_FOR_TRANSPORT_QUERY_KEY,
  DASHBOARD_WARRANTY_QUERY_KEY,
  dashboardSoldQueryKey,
  fetchMasterInventory,
  MASTER_INVENTORY_QUERY_KEY,
} from 'hooks/inventory/fetchMasterInventory'
import {
  fetchDashboardActiveInventory,
  fetchDashboardPurchasedForInventory,
  fetchDashboardReadyForTransport,
  fetchDashboardSoldInRange,
  fetchDashboardWarrantyVehicles,
  registerDashboardVehicleRange,
} from './fetchDashboardVehiclesApi'

export type DashboardDateRange = {
  start: Date
  end: Date
}

const EMPTY_VEHICLES: Vehicle[] = []

export const filterSoldVehiclesInRange = (
  vehicles: Vehicle[],
  dateRange: DashboardDateRange,
): Vehicle[] => {
  const start = dateRange.start
  const end = dateRange.end
  return vehicles
    .filter(vehicle => {
      if (vehicle.status !== 'Sold' || !vehicle.sold_date) return false
      const soldDate = new Date(vehicle.sold_date)
      return soldDate >= start && soldDate <= end
    })
    .sort((a, b) => {
      const dateA = a.sold_date ? new Date(a.sold_date).getTime() : 0
      const dateB = b.sold_date ? new Date(b.sold_date).getTime() : 0
      return dateB - dateA
    })
}

export const filterInventoryVehiclesInRange = (
  vehicles: Vehicle[],
  dateRange: DashboardDateRange,
): Vehicle[] => {
  const start = dateRange.start
  const end = dateRange.end
  return vehicles.filter(vehicle => {
    const vehicleDate = vehicle.date_added ? new Date(vehicle.date_added) : null
    if (!vehicleDate) return true
    return vehicleDate >= start && vehicleDate <= end
  })
}

export const mergeVehiclesById = (...lists: Array<Vehicle[] | undefined>): Vehicle[] => {
  const byId = new Map<string, Vehicle>()
  for (const list of lists) {
    for (const vehicle of list ?? []) {
      if (vehicle.id) byId.set(vehicle.id, vehicle)
    }
  }
  return Array.from(byId.values())
}

const rangeQueryBounds = (dateRange: DashboardDateRange) => ({
  startMs: new Date(
    dateRange.start.getFullYear(),
    dateRange.start.getMonth(),
    dateRange.start.getDate(),
  ).getTime(),
  endMs: new Date(
    dateRange.end.getFullYear(),
    dateRange.end.getMonth(),
    dateRange.end.getDate(),
    23,
    59,
    59,
    999,
  ).getTime(),
})

export const useDashboardSoldQuery = (dateRange: DashboardDateRange) => {
  const { startMs, endMs } = rangeQueryBounds(dateRange)
  return useQuery({
    queryKey: dashboardSoldQueryKey(startMs, endMs),
    queryFn: () => {
      registerDashboardVehicleRange(startMs, endMs)
      return fetchDashboardSoldInRange(new Date(startMs), new Date(endMs))
    },
    ...dashboardQueryOptions,
  })
}

export const useDashboardActiveInventory = (enabled = true) =>
  useQuery({
    queryKey: DASHBOARD_ACTIVE_QUERY_KEY,
    queryFn: fetchDashboardActiveInventory,
    enabled,
    ...dashboardQueryOptions,
  })

/** Sold-in-range plus active unsold — Home lookups that need both. */
export const useDashboardHomeVehicles = (dateRange?: DashboardDateRange) => {
  const bounds = dateRange ? rangeQueryBounds(dateRange) : null
  const sold = useQuery({
    queryKey: dashboardSoldQueryKey(bounds?.startMs ?? 0, bounds?.endMs ?? 0),
    queryFn: () => {
      const startMs = bounds?.startMs ?? 0
      const endMs = bounds?.endMs ?? 0
      registerDashboardVehicleRange(startMs, endMs)
      return fetchDashboardSoldInRange(new Date(startMs), new Date(endMs))
    },
    enabled: Boolean(bounds),
    ...dashboardQueryOptions,
  })
  const active = useDashboardActiveInventory()
  const vehicles = useMemo(
    () => mergeVehiclesById(sold.data, active.data),
    [sold.data, active.data],
  )

  return {
    vehicles,
    soldVehicles: sold.data ?? EMPTY_VEHICLES,
    activeVehicles: active.data ?? EMPTY_VEHICLES,
    isLoading:
      (active.isLoading && !active.data) ||
      (Boolean(bounds) && sold.isLoading && !sold.data),
    isFetching: active.isFetching || (Boolean(bounds) && sold.isFetching),
    isError: sold.isError || active.isError,
  }
}

export const useDashboardWarrantyVehicles = () =>
  useQuery({
    queryKey: DASHBOARD_WARRANTY_QUERY_KEY,
    queryFn: fetchDashboardWarrantyVehicles,
    ...dashboardQueryOptions,
  })

export const useDashboardPurchasedForInventory = (enabled = true) =>
  useQuery({
    queryKey: DASHBOARD_PURCHASED_FOR_INVENTORY_QUERY_KEY,
    queryFn: fetchDashboardPurchasedForInventory,
    enabled,
    ...dashboardQueryOptions,
  })

export const useDashboardReadyForTransport = (enabled = true) =>
  useQuery({
    queryKey: DASHBOARD_READY_FOR_TRANSPORT_QUERY_KEY,
    queryFn: fetchDashboardReadyForTransport,
    enabled,
    ...dashboardQueryOptions,
  })

export const useDashboardInventory = (enabled = true) =>
  useQuery({
    queryKey: MASTER_INVENTORY_QUERY_KEY,
    queryFn: fetchMasterInventory,
    enabled,
    ...dashboardQueryOptions,
  })

/** Full collection — inventory tab and lookups that cannot be scoped. */
export const useDashboardInventoryQuery = (_widgetId?: string) => {
  const query = useDashboardInventory()
  return {
    vehicles: query.data ?? EMPTY_VEHICLES,
    isLoading: query.isLoading && !query.data,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetchVehicles: query.refetch,
  }
}

/** Sold vehicles for the dashboard date range (shared query key, client-filtered). */
export const useDashboardSoldVehicles = (dateRange: DashboardDateRange, _widgetId?: string) => {
  const sold = useDashboardSoldQuery(dateRange)
  const { startMs, endMs } = rangeQueryBounds(dateRange)

  const vehicles = useMemo(() => {
    if (!sold.data) return EMPTY_VEHICLES
    return filterSoldVehiclesInRange(sold.data, {
      start: new Date(startMs),
      end: new Date(endMs),
    })
  }, [sold.data, startMs, endMs])

  return {
    vehicles,
    allVehicles: sold.data ?? EMPTY_VEHICLES,
    isLoading: sold.isLoading && !sold.data,
    isFetching: sold.isFetching,
    isError: sold.isError,
    error: sold.error,
    refetchVehicles: sold.refetch,
  }
}

/** Active (unsold) vehicles, then date_added filter for pending/funded widgets. */
export const useDashboardInventoryVehicles = (dateRange: DashboardDateRange, _widgetId?: string) => {
  const active = useDashboardActiveInventory()
  const { startMs, endMs } = rangeQueryBounds(dateRange)

  const vehicles = useMemo(() => {
    if (!active.data) return EMPTY_VEHICLES
    return filterInventoryVehiclesInRange(active.data, {
      start: new Date(startMs),
      end: new Date(endMs),
    })
  }, [active.data, startMs, endMs])

  return {
    vehicles,
    allVehicles: active.data ?? EMPTY_VEHICLES,
    isLoading: active.isLoading && !active.data,
    isFetching: active.isFetching,
    isError: active.isError,
    error: active.error,
    refetchVehicles: active.refetch,
  }
}

/** True while any of the widget's queries are still loading. */
export const useWidgetLoading = (...loadingFlags: boolean[]): boolean =>
  loadingFlags.some(Boolean)

/** Skip a section re-render when a sibling query updates the parent. */
export const dashboardSectionPropsEqual = <
  T extends { dateRange?: DashboardDateRange; assignedWidgets?: string[] },
>(
  prev: T,
  next: T,
): boolean => {
  const datesMatch =
    !prev.dateRange || !next.dateRange
      ? prev.dateRange === next.dateRange
      : prev.dateRange.start.getTime() === next.dateRange.start.getTime() &&
        prev.dateRange.end.getTime() === next.dateRange.end.getTime()
  if (!datesMatch) return false
  const prevExtra = prev as T & { embedded?: boolean; actionHost?: HTMLElement | null }
  const nextExtra = next as T & { embedded?: boolean; actionHost?: HTMLElement | null }
  if (prevExtra.embedded !== nextExtra.embedded) return false
  if (prevExtra.actionHost !== nextExtra.actionHost) return false
  const left = prev.assignedWidgets
  const right = next.assignedWidgets
  if (left === right) return true
  if (!left || !right || left.length !== right.length) return false
  return left.every((id, index) => id === right[index])
}
